import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Container,
  LinearProgress,
  List,
  ListItem,
  Paper,
  Stack,
  TextField,
  ThemeProvider,
  Typography,
  createTheme,
} from '@mui/material';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { pageColors } from './HomePage';
import apiFetch from '../api/apiFetch';

const UI_VARIANT_KEY = 'uiVariant';
const DEFAULT_UI_VARIANT = 'classic';
const DEFAULT_TOP_LIMIT = Number(process.env.REACT_APP_TOP_LIMIT) || 10;
const DEFAULT_RANKINGS_CONFIG = Object.freeze({
  topSpendersCount: DEFAULT_TOP_LIMIT,
  topSpendersCandidateCount: DEFAULT_TOP_LIMIT,
  topSellersCount: DEFAULT_TOP_LIMIT,
});

const EMPTY_ORDER_SUMS = Object.freeze({
  lastWeekTotal: 0,
  fourWeeksTotal: 0,
  averageFourWeeks: 0,
  lastWeekStart: '',
  lastWeekEnd: '',
  fourWeeksStart: '',
  fourWeeksEnd: '',
});

const STATISTICS_THEME = createTheme({
  palette: {
    primary: {
      main: pageColors.statistics,
    },
  },
  typography: {
    fontFamily: 'Inter, Roboto, sans-serif',
  },
});

const PAGE_BACKGROUND_BY_VARIANT = {
  classic: `${pageColors.statistics}15`,
  modern:
    'radial-gradient(circle at top left, rgba(30,136,229,0.15), transparent 55%), #f5f7fb',
};

const SURFACE_SX = {
  borderRadius: 4,
  backgroundColor: '#fff',
  boxShadow: '0 18px 40px rgba(0,0,0,0.07)',
  border: '1px solid rgba(0,0,0,0.05)',
};

const FILTER_SUMMARY_ITEM_SX = {
  minWidth: 0,
  boxSizing: 'border-box',
  maxWidth: '100%',
  px: 1,
  py: 0.75,
  borderRadius: 2,
  border: '1px solid rgba(0,0,0,0.08)',
  backgroundColor: 'rgba(30,136,229,0.05)',
};

const METRIC_CARD_BASE_SX = {
  ...SURFACE_SX,
  p: { xs: 1.25, md: 2 },
  minWidth: 0,
  height: '100%',
  minHeight: { xs: 156, md: 196 },
  position: 'relative',
  overflow: 'hidden',
  boxShadow: { xs: 'none', md: '0 4px 10px rgba(0,0,0,0.05)' },
};

const RANKING_CARD_SX = {
  ...SURFACE_SX,
  p: { xs: 1.5, md: 2 },
  height: '100%',
  minWidth: 0,
  position: 'relative',
  overflow: 'hidden',
  boxShadow: { xs: 'none', md: '0 4px 10px rgba(0,0,0,0.05)' },
};

const METRIC_CARD_TINTS = {
  lastWeek: {
    up: 'rgba(46,125,50,0.18)',
    down: 'rgba(211,47,47,0.18)',
  },
  average: 'rgba(0,105,92,0.08)',
  total: 'rgba(194,24,91,0.08)',
};

const METRIC_CARD_TREND_STYLES = {
  up: {
    borderColor: 'rgba(46,125,50,0.28)',
    primaryColor: '#1b5e20',
    secondaryColor: 'rgba(27,94,32,0.82)',
  },
  down: {
    borderColor: 'rgba(211,47,47,0.28)',
    primaryColor: '#b71c1c',
    secondaryColor: 'rgba(183,28,28,0.82)',
  },
};

function normalizeUiVariant(value) {
  return value === 'modern' ? 'modern' : DEFAULT_UI_VARIANT;
}

function getStoredUiVariant() {
  if (typeof window === 'undefined') {
    return DEFAULT_UI_VARIANT;
  }

  return normalizeUiVariant(window.localStorage.getItem(UI_VARIANT_KEY));
}

function buildOrderSumsModel(data) {
  return {
    lastWeekTotal: data?.lastWeekTotal || 0,
    fourWeeksTotal: data?.fourWeeksTotal || 0,
    averageFourWeeks: data?.averageFourWeeks || 0,
    lastWeekStart: data?.lastWeekStart || '',
    lastWeekEnd: data?.lastWeekEnd || '',
    fourWeeksStart: data?.fourWeeksStart || '',
    fourWeeksEnd: data?.fourWeeksEnd || '',
  };
}

function formatCurrency(value) {
  if (value == null || Number.isNaN(Number(value))) {
    return 'N/A';
  }

  return Number(value).toLocaleString('en-US', {
    style: 'currency',
    currency: 'SEK',
  });
}

function formatWindow(start, end) {
  if (!start || !end) {
    return 'Not available';
  }

  return `${start} to ${end}`;
}

function formatEntryCount(count) {
  return `${count} ${count === 1 ? 'entry' : 'entries'}`;
}

function getPrimaryStatisticsErrorMessage(status) {
  if (status === 401) {
    return 'Lost connection to Fortnox. Please reconnect and try again.';
  }

  return 'Failed to load statistics. Please try again.';
}

function normalizePositiveInteger(value, fallback) {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue) || numericValue < 0) {
    return fallback;
  }

  return Math.trunc(numericValue);
}

function normalizeRankingsConfig(data) {
  return {
    topSpendersCount: normalizePositiveInteger(
      data?.topSpendersCount,
      DEFAULT_RANKINGS_CONFIG.topSpendersCount
    ),
    topSpendersCandidateCount: normalizePositiveInteger(
      data?.topSpendersCandidateCount,
      DEFAULT_RANKINGS_CONFIG.topSpendersCandidateCount
    ),
    topSellersCount: normalizePositiveInteger(
      data?.topSellersCount,
      DEFAULT_RANKINGS_CONFIG.topSellersCount
    ),
  };
}

const StatisticsMetricCard = memo(function StatisticsMetricCard({
  tint,
  title,
  trend,
  value,
  windowLabel,
}) {
  const trendTone = trend ? METRIC_CARD_TREND_STYLES[trend] : null;

  return (
    <Paper
      elevation={0}
      sx={{
        ...METRIC_CARD_BASE_SX,
        backgroundColor: tint,
        border: trendTone?.borderColor
          ? `1px solid ${trendTone.borderColor}`
          : SURFACE_SX.border,
      }}
    >
      <Stack
        spacing={{ xs: 0.8, md: 1.1 }}
        alignItems="center"
        justifyContent="center"
        textAlign="center"
        sx={{ height: '100%' }}
      >
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="center"
          sx={{ flexWrap: 'wrap' }}
        >
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 700,
              color: trendTone?.primaryColor ?? 'text.secondary',
            }}
          >
            {title}
          </Typography>
          {trend === 'up' ? (
            <TrendingUpIcon
              sx={{
                fontSize: 38,
                color: trendTone?.primaryColor ?? '#2e7d32',
              }}
            />
          ) : trend === 'down' ? (
            <TrendingDownIcon
              sx={{
                fontSize: 38,
                color: trendTone?.primaryColor ?? '#c62828',
              }}
            />
          ) : null}
        </Stack>
        <Typography
          sx={{
            fontWeight: 800,
            fontSize: { xs: '1.75rem', sm: '2.3rem', md: '2.6rem' },
            lineHeight: 1.08,
            overflowWrap: 'anywhere',
            color: trendTone?.primaryColor ?? 'text.primary',
          }}
        >
          {value}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            color: trendTone?.secondaryColor ?? 'text.secondary',
          }}
        >
          {windowLabel}
        </Typography>
      </Stack>
    </Paper>
  );
});

const StatisticsRankingCard = memo(function StatisticsRankingCard({
  emptyMessage,
  headerAction,
  items,
  labelKey,
  title,
  valueKey,
}) {
  return (
    <Paper elevation={0} sx={RANKING_CARD_SX}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        alignItems={{ xs: 'stretch', sm: 'flex-start' }}
        justifyContent="space-between"
        sx={{ mb: 1.5 }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
        {headerAction}
      </Stack>

      <List dense sx={{ p: 0 }}>
        {items.length === 0 ? (
          <ListItem sx={{ px: 0 }}>
            <Typography variant="body2" color="text.secondary">
              {emptyMessage}
            </Typography>
          </ListItem>
        ) : (
          items.map((item, index) => {
            const label = item?.[labelKey] || 'Unknown';
            const key = item?.customerNumber || item?.articleNumber || `${label}-${index}`;

            return (
              <ListItem key={key} sx={{ px: 0, alignItems: 'flex-start' }}>
                <Stack direction="row" spacing={1} sx={{ width: '100%' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 24 }}>
                    {index + 1}.
                  </Typography>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="subtitle2" sx={{ overflowWrap: 'anywhere' }}>
                      {label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatCurrency(item?.[valueKey])}
                    </Typography>
                  </Box>
                </Stack>
              </ListItem>
            );
          })
        )}
      </List>
    </Paper>
  );
});

export default function StatisticsPage({
  connected,
  setConnected,
  setError,
  loadingConnection,
}) {
  const [loading, setLoading] = useState(true);
  const [orderSums, setOrderSums] = useState(EMPTY_ORDER_SUMS);
  const [topSpenderCandidates, setTopSpenderCandidates] = useState([]);
  const [topSellers, setTopSellers] = useState([]);
  const [rankingsConfig, setRankingsConfig] = useState(DEFAULT_RANKINGS_CONFIG);
  const [topSpenderExcludeFilter, setTopSpenderExcludeFilter] = useState('');
  const [uiVariant, setUiVariant] = useState(getStoredUiVariant);
  const hasLoadErrorMessageRef = useRef(false);

  useEffect(() => {
    const handleVariantChange = (event) => {
      const nextVariant =
        event?.detail ??
        (typeof window !== 'undefined'
          ? window.localStorage.getItem(UI_VARIANT_KEY)
          : DEFAULT_UI_VARIANT);

      setUiVariant(normalizeUiVariant(nextVariant));
    };

    const handleStorage = (event) => {
      if (event.key === UI_VARIANT_KEY) {
        setUiVariant(normalizeUiVariant(event.newValue));
      }
    };

    window.addEventListener('ui-variant-changed', handleVariantChange);
    window.addEventListener('storage', handleStorage);

    return () => {
      window.removeEventListener('ui-variant-changed', handleVariantChange);
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  useEffect(() => {
    const clearStatisticsData = () => {
      setOrderSums(EMPTY_ORDER_SUMS);
      setTopSpenderCandidates([]);
      setTopSellers([]);
      setRankingsConfig(DEFAULT_RANKINGS_CONFIG);
    };

    const fetchData = async () => {
      if (loadingConnection) {
        return;
      }

      if (!connected) {
        clearStatisticsData();

        if (!hasLoadErrorMessageRef.current) {
          setError('Not connected to Fortnox. Please connect first.');
        }

        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        hasLoadErrorMessageRef.current = false;

        const [
          orderSumsResponse,
          topSpendersResponse,
          topSellersResponse,
          rankingsConfigResponse,
        ] =
          await Promise.all([
            apiFetch('/api/orders/value-sums'),
            apiFetch('/api/statistics/top-spenders'),
            apiFetch('/api/statistics/top-sellers'),
            apiFetch('/api/config/statistics-rankings'),
          ]);

        if (!orderSumsResponse.ok) {
          clearStatisticsData();
          hasLoadErrorMessageRef.current = true;

          if (orderSumsResponse.status === 401) {
            setConnected(false);
          }

          setError(getPrimaryStatisticsErrorMessage(orderSumsResponse.status));
          return;
        }

        const orderSumsData = await orderSumsResponse.json();
        setOrderSums(buildOrderSumsModel(orderSumsData));

        if (
          topSpendersResponse.status === 401 ||
          topSellersResponse.status === 401 ||
          rankingsConfigResponse.status === 401
        ) {
          clearStatisticsData();
          hasLoadErrorMessageRef.current = true;
          setConnected(false);
          setError(getPrimaryStatisticsErrorMessage(401));
          return;
        }

        let rankingErrorMessage = null;
        let nextRankingsConfig = DEFAULT_RANKINGS_CONFIG;

        if (!rankingsConfigResponse.ok) {
          rankingErrorMessage = 'Statistics ranking configuration could not be loaded. Using default ranking limits.';
        } else {
          const rankingsConfigData = await rankingsConfigResponse.json();
          nextRankingsConfig = normalizeRankingsConfig(rankingsConfigData);
        }

        setRankingsConfig(nextRankingsConfig);

        if (!topSpendersResponse.ok) {
          setTopSpenderCandidates([]);
          rankingErrorMessage = 'Top spenders could not be loaded. Please try again.';
        } else {
          const topSpendersData = await topSpendersResponse.json();
          setTopSpenderCandidates(Array.isArray(topSpendersData) ? topSpendersData : []);
        }

        if (!topSellersResponse.ok) {
          setTopSellers([]);

          if (!rankingErrorMessage) {
            rankingErrorMessage = 'Top sellers could not be loaded. Please try again.';
          }
        } else {
          const topSellersData = await topSellersResponse.json();
          setTopSellers(
            Array.isArray(topSellersData)
              ? topSellersData.slice(0, nextRankingsConfig.topSellersCount)
              : []
          );
        }

        setError(rankingErrorMessage);
      } catch (error) {
        console.error('Failed to load statistics', error);
        clearStatisticsData();
        hasLoadErrorMessageRef.current = true;
        setConnected(false);
        setError(
          'Unable to load statistics right now. Please check your connection and try again.'
        );
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [connected, loadingConnection, setConnected, setError]);

  const pageBackground = PAGE_BACKGROUND_BY_VARIANT[uiVariant];
  const weeklyWindowLabel = formatWindow(orderSums.lastWeekStart, orderSums.lastWeekEnd);
  const fourWeekWindowLabel = formatWindow(orderSums.fourWeeksStart, orderSums.fourWeeksEnd);
  const displayedTopSpenders = useMemo(() => {
    const normalizedFilter = topSpenderExcludeFilter.trim().toLowerCase();
    const filteredCandidates = normalizedFilter
      ? topSpenderCandidates.filter((spender) => {
          const customerName = String(spender?.customerName || '').toLowerCase();
          return !customerName.includes(normalizedFilter);
        })
      : topSpenderCandidates;

    return filteredCandidates.slice(0, rankingsConfig.topSpendersCount);
  }, [rankingsConfig.topSpendersCount, topSpenderCandidates, topSpenderExcludeFilter]);

  const handleTopSpenderExcludeFilterChange = (event) => {
    setTopSpenderExcludeFilter(event.target.value);
  };

  const metricCards = useMemo(
    () => [
      {
        key: 'last-week',
        tint:
          orderSums.lastWeekTotal >= orderSums.averageFourWeeks
            ? METRIC_CARD_TINTS.lastWeek.up
            : METRIC_CARD_TINTS.lastWeek.down,
        title: 'Sales past week',
        trend:
          orderSums.lastWeekTotal >= orderSums.averageFourWeeks ? 'up' : 'down',
        value: formatCurrency(orderSums.lastWeekTotal),
        windowLabel: weeklyWindowLabel,
      },
      {
        key: 'average',
        tint: METRIC_CARD_TINTS.average,
        title: 'Avg weekly sales (4 weeks)',
        trend: null,
        value: formatCurrency(orderSums.averageFourWeeks),
        windowLabel: fourWeekWindowLabel,
      },
      {
        key: 'total',
        tint: METRIC_CARD_TINTS.total,
        title: 'Total sales (4 weeks)',
        trend: null,
        value: formatCurrency(orderSums.fourWeeksTotal),
        windowLabel: fourWeekWindowLabel,
      },
    ],
    [
      fourWeekWindowLabel,
      orderSums.averageFourWeeks,
      orderSums.fourWeeksTotal,
      orderSums.lastWeekTotal,
      weeklyWindowLabel,
    ]
  );

  return (
    <ThemeProvider theme={STATISTICS_THEME}>
      <Box
        sx={{
          minHeight: '100vh',
          background: pageBackground,
          py: { xs: 3, md: 4 },
          px: { xs: 2, sm: 3 },
        }}
      >
        <Container maxWidth="xl">
          <Stack spacing={2.25}>
            <Paper elevation={0} sx={{ ...SURFACE_SX, p: { xs: 1.5, md: 2 } }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Stack direction="row" spacing={1.25} alignItems="center">
                  <Avatar
                    sx={{
                      width: 48,
                      height: 48,
                      bgcolor: 'rgba(30,136,229,0.14)',
                      color: pageColors.statistics,
                    }}
                  >
                    <QueryStatsIcon fontSize="large" />
                  </Avatar>

                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      variant="overline"
                      sx={{ color: pageColors.statistics, letterSpacing: 3 }}
                    >
                      STATISTICS
                    </Typography>
                    <Typography variant="h4" fontWeight={700} sx={{ overflowWrap: 'anywhere' }}>
                      Statistics
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mt: 0.25 }}>
                      Track recent sales totals, top customers, and top-selling articles across
                      the latest reporting windows.
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={{ ...SURFACE_SX, p: { xs: 1.5, md: 1.75 } }}>
              <Stack spacing={1.25}>
                <Box>
                  <Typography variant="h6" fontWeight={600}>
                    Reporting windows
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                    Snapshot totals and ranking lists use the latest week and rolling four-week
                    periods shown here.
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      lg: 'minmax(0, 1fr) auto',
                    },
                    gap: 1.25,
                    alignItems: 'start',
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" color="text.secondary">
                      Top spender rankings show up to{' '}
                      {formatEntryCount(rankingsConfig.topSpendersCount)} from{' '}
                      {rankingsConfig.topSpendersCandidateCount} candidates. Top seller rankings
                      show up to {formatEntryCount(rankingsConfig.topSellersCount)} from the
                      current four-week period.
                    </Typography>
                  </Box>

                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    sx={{
                      width: { xs: '100%', lg: 'auto' },
                      justifySelf: { lg: 'end' },
                    }}
                  >
                    <Box
                      sx={{
                        ...FILTER_SUMMARY_ITEM_SX,
                        width: { xs: '100%', sm: 'auto' },
                        minWidth: { sm: 190 },
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          fontWeight: 700,
                          lineHeight: 1.2,
                          color: pageColors.statistics,
                        }}
                      >
                        Last Week
                      </Typography>
                      <Typography variant="body2" fontWeight={600} color="text.primary">
                        {weeklyWindowLabel}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        ...FILTER_SUMMARY_ITEM_SX,
                        width: { xs: '100%', sm: 'auto' },
                        minWidth: { sm: 210 },
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          fontWeight: 700,
                          lineHeight: 1.2,
                          color: pageColors.statistics,
                        }}
                      >
                        Past 4 Weeks
                      </Typography>
                      <Typography variant="body2" fontWeight={600} color="text.primary">
                        {fourWeekWindowLabel}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              </Stack>
            </Paper>

            {loadingConnection || loading ? (
              <Paper elevation={0} sx={{ ...SURFACE_SX, p: 0, overflow: 'hidden' }}>
                <LinearProgress />
              </Paper>
            ) : !connected ? (
              <Alert severity="info" sx={{ borderRadius: 3 }}>
                Connect to Fortnox to load statistics.
              </Alert>
            ) : (
              <Stack spacing={{ xs: 5, md: 6.25 }}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      md: 'repeat(3, minmax(0, 1fr))',
                    },
                    rowGap: { xs: 5, md: 1.25 },
                    columnGap: 1.25,
                    alignItems: 'stretch',
                  }}
                >
                  {metricCards.map((card) => (
                    <StatisticsMetricCard
                      key={card.key}
                      tint={card.tint}
                      title={card.title}
                      trend={card.trend}
                      value={card.value}
                      windowLabel={card.windowLabel}
                    />
                  ))}
                </Box>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      md: 'repeat(2, minmax(0, 1fr))',
                    },
                    rowGap: { xs: 5, md: 1.25 },
                    columnGap: 1.25,
                    alignItems: 'stretch',
                  }}
                >
                  <StatisticsRankingCard
                    emptyMessage="No top spenders available for this period."
                    headerAction={
                      <TextField
                        label="Exclude customer name"
                        placeholder="Mocca Deli"
                        size="small"
                        value={topSpenderExcludeFilter}
                        onChange={handleTopSpenderExcludeFilterChange}
                        sx={{ width: { xs: '100%', sm: 260 } }}
                      />
                    }
                    items={displayedTopSpenders}
                    labelKey="customerName"
                    title="Top Spenders (4 weeks)"
                    valueKey="totalSales"
                  />
                  <StatisticsRankingCard
                    emptyMessage="No top sellers available for this period."
                    items={topSellers}
                    labelKey="articleName"
                    title="Top Sellers (4 weeks)"
                    valueKey="totalSales"
                  />
                </Box>
              </Stack>
            )}
          </Stack>
        </Container>
      </Box>
    </ThemeProvider>
  );
}
